(() => {
  const roots = document.querySelectorAll('[data-ares-pdp]');
  if (!roots.length) return;

  roots.forEach((root) => {
    const parseData = (name) => {
      try {
        return JSON.parse(root.dataset[name] || 'null');
      } catch (error) {
        console.error(`ARES II PDP: unable to parse ${name}`, error);
        return null;
      }
    };

    const product = parseData('product');
    const mount = parseData('mount');
    if (!product || !Array.isArray(product.variants)) return;

    const stockData = (() => {
      try {
        return JSON.parse(root.querySelector('[data-ares-stock]')?.textContent || '{}');
      } catch (error) {
        console.error('ARES II PDP: unable to parse stock data', error);
        return {};
      }
    })();
    const translations = (() => {
      try {
        return JSON.parse(root.querySelector('[data-ares-translations]')?.textContent || '{}');
      } catch (error) {
        console.error('ARES II PDP: unable to parse translations', error);
        return {};
      }
    })();
    const nativeDeliveryText = root.querySelector('[data-ares-base-delivery]')?.textContent || '';
    const runtimeTranslations = translations.runtime || translations;
    const fallbackTranslations = translations.fallback || {};
    const translate = (key, values = {}) => String(Object.prototype.hasOwnProperty.call(runtimeTranslations, key) ? runtimeTranslations[key] : fallbackTranslations[key] || '').replace(/{{\s*(\w+)\s*}}/g, (_match, name) => values[name] ?? '');
    if (root.style && translations.addingToCart) root.style.setProperty('--ares-pdp-add-loading-label', translations.addingToCart);
    const leadTime = root.dataset.leadTime || translate('defaultLeadTime');
    const inStock = (type, variant) => Boolean(variant && stockData[type] && stockData[type][String(variant.id)]);
    const deliveryText = (type, variant) => inStock(type, variant)
      ? (translate('inStock') || nativeDeliveryText)
      : (translate('expectedToShip', { lead_time: leadTime }) || nativeDeliveryText);

    const currency = root.dataset.currency || 'EUR';
    const selectedVariantId = String(root.dataset.selectedVariantId || '');
    const locale = root.dataset.requestedLocale || root.dataset.locale || document.documentElement.lang || 'en-GB';
    const formatMoney = (cents) => new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((Number(cents) || 0) / 100);

    const state = {
      base: product.variants.find((variant) => String(variant.id) === selectedVariantId)
        || product.variants.find((variant) => variant.available)
        || product.variants[0],
      includeMount: false,
      mount: null,
    };

    const colorButtons = [...root.querySelectorAll('[data-ares-color]')];
    const basePrices = [...root.querySelectorAll('[data-ares-base-price]')];
    const total = root.querySelector('[data-ares-total]');
    const totalRow = root.querySelector('[data-ares-total-row]');
    const mountToggle = root.querySelector('[data-ares-mount-toggle]');
    const addon = root.querySelector('[data-ares-addon]');
    const addonChoice = root.querySelector('[data-ares-addon-choice]');
    const addonLabel = root.querySelector('[data-ares-addon-label]');
    const mountControls = root.querySelector('[data-ares-mount-controls]');
    const mountColor = root.querySelector('[data-ares-mount-color]');
    const mountSize = root.querySelector('[data-ares-mount-size]');
    const mountPrice = root.querySelector('[data-ares-mount-price]');
    const mountLine = root.querySelector('[data-ares-mount-line]');
    const mountPreview = root.querySelector('[data-ares-mount-preview]');
    const mountPreviewName = root.querySelector('[data-ares-mount-preview-name]');
    const mountFit = root.querySelector('[data-ares-mount-fit]');
    const mountFitText = root.querySelector('[data-ares-mount-fit-text]');
    const sizeGuide = root.querySelector('[data-ares-size-guide]');
    const sizeHelp = root.querySelector('[data-ares-size-help]');
    const baseDelivery = root.querySelector('[data-ares-base-delivery]');
    const delivery = root.querySelector('[data-ares-delivery]');
    const form = root.querySelector('[data-ares-form]');
    const button = root.querySelector('[data-ares-add]');
    const status = root.querySelector('[data-ares-status]');
    const quantity = root.querySelector('[data-ares-quantity]');
    const floatingPurchase = root.querySelector('[data-lb-floating-purchase]');
    const floatingPurchaseButton = root.querySelector('[data-lb-floating-purchase-button]');

    const available = (variant) => Boolean(variant && variant.available);
    const syncDeliveryState = (type, variant) => {
      const isStock = inStock(type, variant);
      if (baseDelivery) baseDelivery.textContent = deliveryText(type, variant);
      if (delivery) {
        delivery.classList.toggle('is-in-stock', isStock);
        delivery.classList.toggle('is-preorder', !isStock);
      }
    };
    const selectedColorIndex = () => Number(colorButtons[0] && colorButtons[0].dataset.aresColorIndex) || 0;
    const findBaseVariant = (color) => product.variants.find(
      (variant) => variant.options && variant.options[selectedColorIndex()] === color && available(variant),
    );

    const syncGalleryForColor = () => {
      const activeButton = colorButtons.find((colorButton) => (
        colorButton.dataset.aresColor === state.base.options[selectedColorIndex()]
      ));
      const src = activeButton && activeButton.dataset.aresMediaSrc;
      if (!src) return;

      const image = root.querySelector('[data-ares-gallery-main]');
      if (image) {
        image.src = src;
        image.alt = activeButton.dataset.aresMediaAlt || image.alt;
      }
      root.querySelectorAll('[data-ares-gallery-thumb]').forEach((thumb) => {
        const active = thumb.dataset.aresGallerySrc === src;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    };

    const syncBaseOptions = () => {
      colorButtons.forEach((colorButton) => {
        const active = colorButton.dataset.aresColor === state.base.options[0];
        colorButton.classList.toggle('is-active', active);
        colorButton.setAttribute('aria-pressed', String(active));
      });
      basePrices.forEach((basePrice) => { basePrice.textContent = formatMoney(state.base.price); });
      root.querySelectorAll('[data-ares-selected-color]').forEach((selectedName) => {
        selectedName.textContent = state.base.options[selectedColorIndex()];
      });
      syncDeliveryState('base', state.base);
      syncGalleryForColor();
    };

    const mountVariants = mount && Array.isArray(mount.variants) ? mount.variants : [];
    const syncMountOptions = () => {
      if (!mountColor || !mountSize || !mountVariants.length) return;
      const selectedColor = mountColor.value;
      const matching = mountVariants.filter((variant) => available(variant) && variant.options[0] === selectedColor);

      [...mountSize.options].forEach((option) => {
        option.disabled = !matching.some((variant) => variant.options[1] === option.value);
      });
      if (mountSize.selectedOptions[0] && mountSize.selectedOptions[0].disabled) {
        const firstSize = [...mountSize.options].find((option) => !option.disabled);
        if (firstSize) mountSize.value = firstSize.value;
      }
      state.mount = mountVariants.find((variant) => available(variant)
        && variant.options[0] === mountColor.value
        && variant.options[1] === mountSize.value) || null;
      if (mountPrice) mountPrice.textContent = state.mount ? formatMoney(state.mount.price) : translate('unavailable');

      const selectedName = `${mountColor.value} · ${mountSize.value}`;
      if (mountPreviewName) mountPreviewName.textContent = state.mount ? selectedName : translate('selectionUnavailable', { selection: selectedName });
      if (mountFit) {
        const isMountStock = inStock('mount', state.mount);
        mountFit.classList.toggle('is-in-stock', isMountStock);
        mountFit.classList.toggle('is-preorder', !isMountStock);
        const mountDelivery = state.mount
          ? deliveryText('mount', state.mount)
          : translate('chooseAvailableMount');
        if (mountFitText) mountFitText.textContent = mountDelivery;
        else mountFit.textContent = mountDelivery;
      }
      const previewMedia = state.mount && (state.mount.featured_media || state.mount.featured_image);
      const previewImage = previewMedia && (previewMedia.preview_image || previewMedia);
      if (mountPreview && previewImage && previewImage.src) {
        mountPreview.src = previewImage.src;
        const mountTitle = mount.title || translate('mountAltFallback');
        mountPreview.alt = `${mountTitle} — ${selectedName}`;
      }
    };

    const syncCartSummary = () => {
      const addMount = state.includeMount && state.mount;
      const baseCents = Number(state.base.price) || 0;
      const mountCents = addMount ? Number(state.mount.price) || 0 : 0;
      const mountDelaysOrder = addMount && !inStock('mount', state.mount);
      const deliveryType = mountDelaysOrder ? 'mount' : 'base';
      const deliveryVariant = mountDelaysOrder ? state.mount : state.base;
      if (mountLine) mountLine.hidden = !addMount;
      if (totalRow) totalRow.hidden = !addMount;
      if (total) total.textContent = formatMoney(baseCents + mountCents);
      syncDeliveryState(deliveryType, deliveryVariant);
      if (button) button.disabled = !available(state.base) || (state.includeMount && !state.mount);
    };

    const sync = () => {
      syncBaseOptions();
      syncMountOptions();
      syncCartSummary();
    };

    colorButtons.forEach((colorButton) => {
      colorButton.addEventListener('click', () => {
        const variant = findBaseVariant(colorButton.dataset.aresColor);
        if (!variant) return;
        state.base = variant;
        sync();
      });
    });

    const syncMountToggleUI = () => {
      if (!mountToggle) return;
      state.includeMount = mountToggle.checked;
      if (mountControls) mountControls.hidden = !state.includeMount;
      mountToggle.setAttribute('aria-expanded', String(state.includeMount));
      if (addon) addon.classList.toggle('is-selected', state.includeMount);
      if (addonChoice) {
        addonChoice.setAttribute('aria-expanded', String(state.includeMount));
        addonChoice.setAttribute('aria-pressed', String(state.includeMount));
      }
      if (addonLabel) addonLabel.textContent = state.includeMount ? translate('added') : translate('addMount');
    };

    if (mountToggle && mountControls) {
      mountToggle.addEventListener('change', () => {
        syncMountToggleUI();
        sync();
      });
    }
    if (addonChoice && mountToggle) {
      const toggleMount = () => {
        mountToggle.checked = !mountToggle.checked;
        mountToggle.dispatchEvent(new Event('change', { bubbles: true }));
      };
      addonChoice.addEventListener('click', toggleMount);
      addonChoice.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggleMount();
      });
    }
    if (sizeGuide && sizeHelp) {
      const syncSizeHelpState = () => sizeHelp.setAttribute('aria-expanded', String(sizeGuide.open));
      sizeGuide.addEventListener('toggle', syncSizeHelpState);
      sizeHelp.addEventListener('click', () => {
        sizeGuide.open = true;
        syncSizeHelpState();
        requestAnimationFrame(() => {
          sizeGuide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          sizeGuide.querySelector('summary')?.focus({ preventScroll: true });
        });
      });
      syncSizeHelpState();
    }
    syncMountToggleUI();
    [mountColor, mountSize].filter(Boolean).forEach((input) => input.addEventListener('change', sync));

    const syncFloatingPurchase = () => {
      if (!floatingPurchase || !form) return;
      floatingPurchase.hidden = form.getBoundingClientRect().bottom >= 0;
    };
    if (floatingPurchase && floatingPurchaseButton) {
      floatingPurchaseButton.addEventListener('click', () => button?.click());
      window.addEventListener('scroll', syncFloatingPurchase, { passive: true });
      window.addEventListener('resize', syncFloatingPurchase);
      syncFloatingPurchase();
    }

    root.querySelectorAll('[data-ares-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const image = root.querySelector('[data-ares-gallery-main]');
        if (!image) return;
        image.src = thumb.dataset.aresGallerySrc;
        image.alt = thumb.dataset.aresGalleryAlt || image.alt;
        root.querySelectorAll('[data-ares-gallery-thumb]').forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
      });
    });

    const galleryFrame = root.querySelector('.ares-pdp__media-main');
    const galleryThumbs = [...root.querySelectorAll('[data-ares-gallery-thumb]')];
    if (galleryFrame && galleryThumbs.length > 1) {
      let touchStartX = null;
      galleryFrame.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0]?.clientX ?? null;
      }, { passive: true });
      galleryFrame.addEventListener('touchend', (event) => {
        const touchEndX = event.changedTouches[0]?.clientX;
        if (touchStartX === null || touchEndX === undefined) return;
        const delta = touchEndX - touchStartX;
        touchStartX = null;
        if (Math.abs(delta) < 42) return;
        const activeIndex = Math.max(0, galleryThumbs.findIndex((thumb) => thumb.classList.contains('is-active')));
        const nextIndex = Math.max(0, Math.min(activeIndex + (delta < 0 ? 1 : -1), galleryThumbs.length - 1));
        if (nextIndex === activeIndex) return;
        const nextThumb = galleryThumbs[nextIndex];
        nextThumb.click();
        nextThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }, { passive: true });
      galleryFrame.addEventListener('touchcancel', () => { touchStartX = null; }, { passive: true });
    }

    if (form && button) {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (button.disabled || !state.base) return;
        const qty = Math.max(1, Number(quantity && quantity.value) || 1);
        const items = [{ id: state.base.id, quantity: qty }];
        if (state.includeMount && state.mount) items.push({ id: state.mount.id, quantity: qty });

        button.disabled = true;
        if (status) status.textContent = '';
        try {
          const response = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          if (!response.ok) throw new Error('Cart request failed');
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.refresh === 'function' && typeof drawer.open === 'function') {
            await drawer.refresh(true);
            drawer.open(button);
          } else {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { detail: { open: true } }));
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: button } }));
          }
          if (floatingPurchase) floatingPurchase.hidden = true;
          button.classList.add('is-confirmed');
          window.setTimeout(() => {
            button.classList.remove('is-confirmed');
            button.disabled = false;
          }, 560);
        } catch (error) {
          console.error('LB Bluetooth PDP cart error', error);
          if (status) status.textContent = translate('cartError');
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      });
    }

    const videoPlayer = root.querySelector('[data-ares-video-player]');
    if (videoPlayer) {
      let videos = [];
      try {
        videos = JSON.parse(videoPlayer.dataset.aresVideos || '[]');
      } catch (error) {
        console.error('ARES II PDP: unable to parse video playlist', error);
      }
      const videoFrame = videoPlayer.querySelector('[data-ares-video]');
      const videoLoad = videoPlayer.querySelector('[data-ares-video-load]');
      const previous = videoPlayer.querySelector('[data-ares-video-prev]');
      const next = videoPlayer.querySelector('[data-ares-video-next]');
      const title = videoPlayer.querySelector('[data-ares-video-title]');
      const position = videoPlayer.querySelector('[data-ares-video-position]');
      const items = [...videoPlayer.querySelectorAll('[data-ares-video-item]')];
      let index = 0;

      const updatePlaylistUI = () => {
        const current = videos[index];
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index === videos.length - 1;
        if (title) title.textContent = current.title;
        if (position) position.textContent = translate('videoPosition', { current: index + 1, total: videos.length });
        items.forEach((item) => {
          const active = Number(item.dataset.aresVideoIndex) === index;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
        });
      };

      const loadVideo = (requestedIndex, autoplay = true) => {
        if (!videoFrame || !videos.length) return;
        index = Math.max(0, Math.min(requestedIndex, videos.length - 1));
        const current = videos[index];
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${current.id}?rel=0&modestbranding=1${autoplay ? '&autoplay=1' : ''}`;
        iframe.title = translate('iframeTitle', { title: current.title });
        iframe.loading = 'eager';
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        videoFrame.replaceChildren(iframe);
        updatePlaylistUI();
      };

      if (videos.length) {
        updatePlaylistUI();
        videoLoad?.addEventListener('click', () => loadVideo(index));
        previous?.addEventListener('click', () => loadVideo(index - 1));
        next?.addEventListener('click', () => loadVideo(index + 1));
        items.forEach((item) => item.addEventListener('click', () => loadVideo(Number(item.dataset.aresVideoIndex))));
      }
    }

    sync();
  });
})();
